/**
 * «Questo bot sta lavorando, e quanto vale adesso?»
 *
 * Due domande che l'interfaccia rispondeva male entrambe, e per la stessa ragione: leggeva
 * `updatedAt` come se fosse un battito cardiaco.
 *
 * ## Cosa era rotto, visto in produzione
 *
 * `updatedAt` dice l'ultima volta che una colonna e' CAMBIATA. Il bot 12 teneva una posizione
 * aperta da cinque giorni: la posizione non cambia, i saldi non cambiano, quindi TypeORM non
 * emetteva nemmeno la UPDATE e `updatedAt` restava fermo al giorno dell'ultima variazione. La
 * schermata ne ricavava «il bot non scrive da 4g 22h. Questi prezzi e questi saldi non sono di
 * adesso: non decidere su questi numeri» — su un bot vivo, con denaro vero, che stava facendo
 * esattamente il suo mestiere. Il bot piu' sano risultava il piu' abbandonato, e l'avvertenza
 * invitava a diffidare dell'unico numero corretto della pagina.
 *
 * Ora il backend scrive `lastSeenAt` a ogni candela ricevuta, con una query che NON tocca
 * `updatedAt`. Le due colonne rispondono a due domande diverse e vanno lette come tali:
 *
 * | colonna | domanda |
 * | --- | --- |
 * | `updatedAt` | quando e' cambiato qualcosa |
 * | `lastSeenAt` | quando il processo era vivo |
 *
 * ## La soglia, e da dove viene
 *
 * Venti minuti. Non e' scelta a occhio: e' la stessa soglia con cui l'healthcheck del container
 * in produzione decide che le candele sono stantie (`CANDLE_MAX_AGE_MIN=20`). Il battito e' guidato
 * dallo scarico delle candele, quindi si ferma sia se muore il bot sia se muore il flusso dati:
 * due guasti diversi che chiedono entrambi di essere guardati, e che questa schermata non puo'
 * distinguere. Dirlo e' piu' onesto che sceglierne uno.
 */
import type { Tradebot } from '../api/tipi';
import { daQuanto } from './formato';

/** Oltre questi minuti senza battito il bot non e' piu' «visto ora». Vedi sopra. */
const MINUTI_FRESCO = 20;
/** Oltre questi, non e' piu' un ritardo: e' un silenzio. */
const MINUTI_SILENZIO = 120;

export type StatoBattito =
  /** Fermo per scelta: la domanda «e' vivo?» non si pone. */
  | 'fermo'
  /** Visto entro la soglia: quello che mostra la pagina e' di adesso. */
  | 'vivo'
  /** In marcia, ma il battito e' in ritardo. Puo' essere il bot o il flusso dati. */
  | 'in-ritardo'
  /** In marcia da database, ma nessun segno di vita da ore. */
  | 'muto'
  /** Nessun battito mai registrato: non si afferma niente. */
  | 'non-misurabile';

export interface Vitalita {
  stato: StatoBattito;
  /** L'istante dell'ultimo battito, se c'e'. */
  visto: Date | null;
  /** Frase pronta, gia' al punto: niente gerghi, niente nomi di colonne. */
  testo: string;
  /** Vero solo quando c'e' davvero qualcosa da guardare: guida l'avvertenza. */
  allarme: boolean;
}

function data(v: string | Date | null | undefined): Date | null {
  if (!v) return null;
  const d = v instanceof Date ? v : new Date(v);
  return Number.isNaN(d.getTime()) ? null : d;
}

export function vitalitaDi(bot: Tradebot, adesso = Date.now()): Vitalita {
  if (!bot.isRunning) {
    return {
      stato: 'fermo',
      visto: data(bot.lastSeenAt),
      testo: 'fermo: nessun processo in ascolto',
      allarme: false,
    };
  }

  const visto = data(bot.lastSeenAt);
  if (!visto) {
    /* Nessun battito. Il caso normale e' un bot avviato prima che la colonna esistesse, o appena
       avviato e non ancora arrivato alla prima candela. Non si trasforma un'assenza di misura in
       un allarme: si dice che non si sa. */
    return {
      stato: 'non-misurabile',
      visto: null,
      testo: 'in marcia da registro; il battito non è ancora stato scritto',
      allarme: false,
    };
  }

  const minuti = (adesso - visto.getTime()) / 60000;
  if (minuti <= MINUTI_FRESCO) {
    return { stato: 'vivo', visto, testo: `visto ${daQuanto(visto, adesso)}`, allarme: false };
  }
  if (minuti <= MINUTI_SILENZIO) {
    return {
      stato: 'in-ritardo',
      visto,
      testo: `visto ${daQuanto(visto, adesso)}: si è fermato il bot o il flusso delle candele`,
      allarme: true,
    };
  }
  return {
    stato: 'muto',
    visto,
    testo: `risulta in marcia, ma l’ultimo segno di vita è ${daQuanto(visto, adesso)}`,
    allarme: true,
  };
}

/* ------------------------------------------------------------------ capitale */

export interface CapitaleBot {
  /** Il totale in valuta, o `null` se non e' calcolabile. Mai zero per «non lo so». */
  valore: number | null;
  /** Il prezzo usato per valutare l'asset, e da dove viene. */
  prezzo: number | null;
  fontePrezzo: 'battito' | 'ultima-operazione' | 'avvio' | null;
  /** Vero se manca un pezzo: il numero c'e' ma non e' tutto. */
  parziale: boolean;
  /** Perche' e' parziale o perche' manca, in una frase. `null` se e' completo. */
  motivo: string | null;
}

/**
 * Il capitale governato dal bot, valutato al prezzo piu' recente che si conosce.
 *
 * Prima si sommava la sola valuta, perche' un prezzo per l'asset non c'era: un bot con
 * venticinquemila dollari di BTCB e cinque centesimi di USDT risultava «0,00 USDT · parziale».
 * Esatto e inutile — e su una schermata che serve a decidere, un numero inutile e' peggio di
 * nessun numero, perche' occupa il posto di quello giusto.
 *
 * I tre prezzi in ordine di preferenza, e la fonte viene dichiarata perche' cambia quanto ci si
 * puo' fidare: `lastSeenPrice` e' di minuti fa, `finalAssetPrice` e' dell'ultima operazione
 * chiusa, `initialAssetPrice` e' di quando il bot e' partito e puo' essere vecchio di mesi.
 */
/**
 * Distingue «non l'ho letto» da «vale zero».
 *
 * Il server risponde solo con i campi chiesti nella proiezione: un campo non chiesto arriva
 * `undefined`, un campo chiesto e vuoto a database arriva `null`. Sono due cose diverse e vanno
 * tenute diverse, perche' `undefined ?? 0` produce uno zero indistinguibile da un saldo davvero
 * azzerato -- e uno zero finto dentro una somma la abbassa senza lasciare traccia.
 */
function letto(v: number | null | undefined): v is number {
  return typeof v === 'number' && Number.isFinite(v);
}

export function capitaleDi(bot: Tradebot): CapitaleBot {
  /* `parkedCurrency` e' l'unico dei tre in cui `null` significa davvero zero: e' una colonna che
     solo il trader a quota di saldo riempie, e per tutti gli altri bot la sua assenza non e'
     ignoranza, e' un fatto. `undefined` invece resta ignoranza anche qui. */
  const parcheggiata = bot.parkedCurrency === undefined ? undefined : (bot.parkedCurrency ?? 0);

  const asset = letto(bot.assetBalance) ? bot.assetBalance : null;
  const valuta = letto(bot.currencyBalance) ? bot.currencyBalance : null;
  const parked = letto(parcheggiata) ? parcheggiata : null;

  const assetIgnoto = asset === null;
  /* La valuta e' nota solo se lo sono entrambe le sue componenti: sommarne una e ignorare l'altra
     darebbe un totale piu' basso del vero, dichiarato completo. */
  const valutaIgnota = valuta === null || parked === null;
  const totaleValuta = valutaIgnota ? 0 : (valuta as number) + (parked as number);

  let prezzo: number | null = null;
  let fonte: CapitaleBot['fontePrezzo'] = null;
  if (letto(bot.lastSeenPrice) && bot.lastSeenPrice > 0) {
    prezzo = bot.lastSeenPrice;
    fonte = 'battito';
  } else if (letto(bot.finalAssetPrice) && bot.finalAssetPrice > 0) {
    prezzo = bot.finalAssetPrice;
    fonte = 'ultima-operazione';
  } else if (letto(bot.initialAssetPrice) && bot.initialAssetPrice > 0) {
    prezzo = bot.initialAssetPrice;
    fonte = 'avvio';
  }

  const nota =
    fonte === 'avvio'
      ? 'valutato al prezzo di quando il bot è partito: può essere vecchio'
      : fonte === 'ultima-operazione'
        ? "valutato al prezzo dell'ultima operazione chiusa"
        : null;

  /* Niente di leggibile: non si inventa uno zero, che si leggerebbe come «ha perso tutto». */
  if (assetIgnoto && valutaIgnota) {
    return {
      valore: null,
      prezzo,
      fontePrezzo: fonte,
      parziale: false,
      motivo: 'i saldi non sono ancora stati registrati',
    };
  }

  /* Asset noto e pari a zero: il bot e' fuori mercato, il prezzo non serve e il totale e' intero.
     Vale solo perche' `asset` qui e' un numero LETTO, non un `undefined` mascherato da zero. */
  if (asset === 0) {
    return {
      valore: valutaIgnota ? null : totaleValuta,
      prezzo,
      fontePrezzo: fonte,
      parziale: false,
      motivo: valutaIgnota ? 'il saldo in valuta non è stato letto' : null,
    };
  }

  if (assetIgnoto) {
    return {
      valore: totaleValuta,
      prezzo,
      fontePrezzo: fonte,
      parziale: true,
      motivo: `manca il saldo in ${bot.asset}`,
    };
  }

  if (prezzo === null) {
    /* Se la parte calcolabile e' esattamente zero non si scrive «0,00»: e' il numero che
       l'interfaccia mostrava per un bot che teneva duemilaottocento dollari di BTCB, e si legge
       come «non hai niente». Meglio nessuna cifra e il motivo. */
    const calcolabile = valutaIgnota || totaleValuta === 0 ? null : totaleValuta;
    return {
      valore: calcolabile,
      prezzo: null,
      fontePrezzo: null,
      parziale: calcolabile !== null,
      motivo: `manca un prezzo per valutare ${asset} ${bot.asset}`,
    };
  }

  const inAsset = (asset as number) * prezzo;
  if (valutaIgnota) {
    return {
      valore: inAsset,
      prezzo,
      fontePrezzo: fonte,
      parziale: true,
      motivo: 'manca il saldo in valuta: qui c’è solo la parte investita',
    };
  }

  return {
    valore: totaleValuta + inAsset,
    prezzo,
    fontePrezzo: fonte,
    parziale: false,
    motivo: nota,
  };
}

/* ------------------------------------------------------------------ ordine */

/**
 * L'ordine in cui i bot vanno guardati: prima chi lavora.
 *
 * Prima si ordinava per id decrescente, cioe' per data di creazione. Con undici bot fermi e uno
 * in marcia, il criterio metteva in cima l'ultimo creato — che per fortuna era anche l'unico
 * vivo, ma per coincidenza. Un bot spento il mese scorso non ha niente da dire; quello che sta
 * muovendo denaro adesso ha tutto.
 *
 * A parita' di stato resta l'id decrescente: e' l'ordine che l'utente conosce gia'.
 */
export function ordinaPerImportanza(bots: Tradebot[]): Tradebot[] {
  /* Un solo `adesso` per tutto l'ordinamento, e una sola lettura per bot. Chiamare `vitalitaDi`
     dentro il comparatore la rieseguiva O(n log n) volte, ognuna con un istante leggermente
     diverso: a dodici bot non cambiava niente, ma e' un ordine che dipende da quando lo si
     calcola, e non e' una proprieta' che si vuole avere. */
  const adesso = Date.now();
  const peso = new Map<number, number>();
  for (const b of bots) {
    const stato = vitalitaDi(b, adesso).stato;
    const reale = b.useRealMoney;
    peso.set(
      b.id,
      stato === 'muto' || stato === 'in-ritardo'
        ? reale
          ? 0
          : 1 // in marcia e in silenzio: prima di tutto
        : stato === 'vivo' || stato === 'non-misurabile'
          ? reale
            ? 2
            : 3
          : 4, // fermo
    );
  }
  return [...bots].sort((a, b) => (peso.get(a.id) ?? 4) - (peso.get(b.id) ?? 4) || b.id - a.id);
}
